import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel";
import {
	TransformedConfigurationComponent_SubConfigurator,
	TransformedConfigurationComponent_SubConfiguratorList,
} from "src/api/generated/erp/configurator/componentTransformation/model/transformedConfigurationComponent.ts";
import { getConfiguratorFormErrors } from "src/components/views/erp/configurator/configuratorForm/validation.ts";
import { ConfiguratorTreeItemInputErrors } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/ConfiguratorTreeItemInputErrors.ts";
import { filterNulls } from "src/utils/arrayUtils.ts";
import {
	ConfiguratorInput,
	ConfiguratorInput_Component,
} from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { getSubConfiguratorVersion } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";

export function findTreeItem(rootItem: ConfiguratorTreeItemModel, key: string): ConfiguratorTreeItemModel | undefined {
	if (rootItem.key === key) {
		return rootItem;
	}

	for (const child of rootItem.children) {
		const result = findTreeItem(child, key);
		if (result) {
			return result;
		}
	}

	return undefined;
}

export function findTreeItemParent(
	rootItem: ConfiguratorTreeItemModel,
	childKey: string,
): ConfiguratorTreeItemModel | undefined {
	for (const child of rootItem.children) {
		if (child.key === childKey) {
			return rootItem;
		}

		const result = findTreeItemParent(child, childKey);
		if (result) {
			return result;
		}
	}

	return undefined;
}

export function getConfiguratorTreeRecursiveInputErrors(
	treeItem: ConfiguratorTreeItemModel,
): ConfiguratorTreeItemInputErrors | undefined {
	const ownErrors = getConfiguratorFormErrors(treeItem.components);
	const children = filterNulls(treeItem.children.map((child) => getConfiguratorTreeRecursiveInputErrors(child)));
	if (ownErrors.length === 0 && children.length === 0) {
		return undefined;
	}

	return {
		label: treeItem.label,
		errors: ownErrors,
		children: children,
	};
}

export function getAnyAncestorHasErrors(rootItem: ConfiguratorTreeItemModel, itemKey: string): boolean {
	return getAnyAncestorHasErrorsHelper(rootItem, itemKey, false) ?? false;
}

function getAnyAncestorHasErrorsHelper(
	currentItem: ConfiguratorTreeItemModel,
	targetItemKey: string,
	currentBranchHasErrors: boolean,
): boolean | undefined {
	if (targetItemKey === currentItem.key) return currentBranchHasErrors;

	const currentItemHasErrors = getConfiguratorFormErrors(currentItem.components).length > 0;
	const nextBranchHasErrors = currentBranchHasErrors || currentItemHasErrors;

	for (const child of currentItem.children) {
		const resultFromChildPath = getAnyAncestorHasErrorsHelper(child, targetItemKey, nextBranchHasErrors);
		if (resultFromChildPath != null) return resultFromChildPath;
	}

	// Undefined means that target isn't found from this branch
	return undefined;
}

export function getTreeItemSubConfiguratorComponents(
	treeItem: ConfiguratorTreeItemModel,
): TransformedSubConfiguratorOrListComponent[] {
	return treeItem.components.filter(
		(component) => component.type === "subConfigurator" || component.type === "subConfiguratorList",
	);
}

export type TransformedSubConfiguratorOrListComponent =
	| TransformedConfigurationComponent_SubConfigurator
	| TransformedConfigurationComponent_SubConfiguratorList;

export function getAddableTreeItemSubConfiguratorComponents(
	treeItem: ConfiguratorTreeItemModel,
): TransformedSubConfiguratorOrListComponent[] {
	const allSubConfiguratorComponents = getTreeItemSubConfiguratorComponents(treeItem);
	const alreadyAddedSubConfiguratorComponentNames = treeItem.children.map(
		(subConfigurator) => subConfigurator.selfConfiguratorComponentName,
	);
	return allSubConfiguratorComponents.filter((subConfiguratorComponent) => {
		if (!subConfiguratorComponent.visible) return false;

		switch (subConfiguratorComponent.type) {
			case "subConfiguratorList":
				return subConfiguratorComponent.addEnabled;
			case "subConfigurator":
				return (
					subConfiguratorComponent.addEnabled &&
					!alreadyAddedSubConfiguratorComponentNames.includes(subConfiguratorComponent.name)
				);
		}
	});
}

/**
 * Sub-configurators that can still be selected in the bulk add dialog. A single-instance sub-configurator can be
 * selected only once, because the configurator input holds only one child for it.
 */
export function getAddableTreeItemSubConfiguratorComponentsForBulkSelection(
	treeItem: ConfiguratorTreeItemModel,
	selectedComponents: TransformedSubConfiguratorOrListComponent[],
): TransformedSubConfiguratorOrListComponent[] {
	const selectedSingleComponentNames = new Set(
		selectedComponents
			.filter((component) => component.type === "subConfigurator")
			.map((component) => component.name),
	);
	return getAddableTreeItemSubConfiguratorComponents(treeItem).filter(
		(component) => component.type === "subConfiguratorList" || !selectedSingleComponentNames.has(component.name),
	);
}

export function getTreeItemConfiguratorInput(treeItem: ConfiguratorTreeItemModel): ConfiguratorInput {
	return {
		inputComponentValues: treeItem.components.map((component) => ({
			componentName: component.name,
			value: component.currentValue,
		})),
	};
}

export function getTreeItemRecursiveConfiguratorInput(treeItem: ConfiguratorTreeItemModel): ConfiguratorInput {
	const scalarInputComponentValues = treeItem.components
		.filter((c) => c.type !== "subConfigurator" && c.type !== "subConfiguratorList")
		.map((component) => ({
			componentName: component.name,
			value: component.currentValue,
		}));

	const inputComponentValuesFromSubConfigurators: ConfiguratorInput_Component[] = treeItem.components
		.filter((c) => c.type === "subConfigurator")
		.map((subConfiguratorComponent) => {
			const child = treeItem.children.find(
				(c) => c.selfConfiguratorComponentName === subConfiguratorComponent.name,
			);

			const childConfiguratorInput = child == null ? null : getTreeItemRecursiveConfiguratorInput(child);

			return {
				componentName: subConfiguratorComponent.name,
				value: {
					type: "SUB_CONFIGURATION",
					subConfiguratorInput: childConfiguratorInput,
					configuratorVersion: getSubConfiguratorVersion(child?.configuratorType),
				},
			};
		});

	const inputComponentValuesFromSubConfiguratorLists: ConfiguratorInput_Component[] = treeItem.components
		.filter((c) => c.type === "subConfiguratorList")
		.map((subConfiguratorComponent) => {
			const children = treeItem.children.filter(
				(c) => c.selfConfiguratorComponentName === subConfiguratorComponent.name,
			);

			return {
				componentName: subConfiguratorComponent.name,
				value: {
					type: "SUB_CONFIGURATION_LIST",
					subConfiguratorInputs: children.map((child) => ({
						type: "SUB_CONFIGURATION",
						subConfiguratorInput: getTreeItemRecursiveConfiguratorInput(child),
						configuratorVersion: getSubConfiguratorVersion(child.configuratorType),
					})),
				},
			};
		});

	return {
		inputComponentValues: [
			...scalarInputComponentValues,
			...inputComponentValuesFromSubConfigurators,
			...inputComponentValuesFromSubConfiguratorLists,
		],
	};
}

export function getTreeItemWithParentProperties(
	rootItem: ConfiguratorTreeItemModel,
	treeItemKey: string,
): ConfiguratorTreeItemModel | undefined {
	const treeItem = findTreeItem(rootItem, treeItemKey);
	const parentItem = findTreeItemParent(rootItem, treeItemKey);

	if (treeItem == null) return undefined;
	if (parentItem == null) return treeItem;

	if (treeItem.configuratorType.type !== "subConfigurator") {
		return treeItem;
	}

	return {
		...treeItem,
		configuratorType: {
			...treeItem.configuratorType,
			parentProperties: parentItem.propertyValues,
		},
	};
}
